import * as React from "react";
import { connect, ConnectedProps } from "react-redux";

import InitialSetupStep from "components/products/bmwe-home-page/InitialSetupSetup";
import InstallBmweStep from "components/products/bmwe-home-page/InstallBmweStep";
import ChooseWorkflowTemplate from "components/products/choose-workflow-template/ChooseWorkflowTemplate";
import RunErasure from "components/products/onboarding-run-erasure/RunErasure";
import ViewReport from "components/products/onboarding-view-report/ViewReport";
import { LicenseData } from "domain/licenses";
import {
    BmweOnboardingSteps,
    checkIfMenuExpanded,
    checkIfStepIsClickable,
    getStepIndexForBmwe,
    isStepCompleted,
    OnboardingProductType,
} from "domain/onboardings";
import { OnboardingImage, SoftwarePackage, Versions } from "domain/softwarePackages";
import { TemplateTableData } from "domain/workflowTemplates";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";

interface Props {
    lastCompletedStepIndex: number;
    fetchedSoftwarePackages?: SoftwarePackage[];
    defaultWorkflowTemplate: TemplateTableData;
    requestFailureMessage?: string;
    licenses: Versions[];
    fetchedLicenses: LicenseData[];
    canViewReport: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    hasIbr: boolean;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedOnboardingImages: OnboardingImage[];
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});
const connector = connect(mapState);

const BmweSteps = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const getStepIndex = (step: BmweOnboardingSteps | undefined) => {
        return getStepIndexForBmwe(step, props.hasIbr);
    };

    const [showWorkflowStep, setShowWorkflowStep] = React.useState(false);

    React.useEffect(() => {
        props.hasIbr && setShowWorkflowStep(true);
    }, []);

    return (
        <>
            {props.hasIbr && (
                <ChooseWorkflowTemplate
                    stepIndex={getStepIndex(BmweOnboardingSteps.STEP_WORKFLOW_TEMPLATE)}
                    menuExpanded={checkIfMenuExpanded(
                        props.lastCompletedStepIndex,
                        getStepIndex(BmweOnboardingSteps.STEP_WORKFLOW_TEMPLATE),
                        props.hasIbr
                    )}
                    stepCompleted={isStepCompleted(
                        getStepIndex(BmweOnboardingSteps.STEP_WORKFLOW_TEMPLATE),
                        props.lastCompletedStepIndex
                    )}
                    stepActive={true}
                    product={OnboardingProductType.BMWE}
                    defaultWorkflowTemplate={props.defaultWorkflowTemplate}
                    requestFailureMessage={props.requestFailureMessage}
                    saveOnboardingStep={props.saveOnboardingStep}
                    stepInProgressIndex={props.stepInProgressIndex}
                    saveStepInProgressIndex={props.saveStepInProgressIndex}
                />
            )}

            <InstallBmweStep
                licenses={props.licenses}
                stepIndex={getStepIndex(BmweOnboardingSteps.STEP_INSTALLING_BMWE)}
                fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                fetchedLicenses={props.fetchedLicenses}
                featureWorkflowLicensePresent={showWorkflowStep}
                stepCompleted={isStepCompleted(
                    getStepIndex(BmweOnboardingSteps.STEP_INSTALLING_BMWE),
                    props.lastCompletedStepIndex
                )}
                stepActive={!props.hasIbr}
                stepClickable={checkIfStepIsClickable(
                    getStepIndex(BmweOnboardingSteps.STEP_INSTALLING_BMWE),
                    props.lastCompletedStepIndex,
                    props.hasIbr
                )}
                saveOnboardingStep={props.saveOnboardingStep}
                menuExpanded={checkIfMenuExpanded(
                    props.lastCompletedStepIndex,
                    getStepIndex(BmweOnboardingSteps.STEP_INSTALLING_BMWE),
                    props.hasIbr
                )}
                stepInProgressIndex={props.stepInProgressIndex}
                saveStepInProgressIndex={props.saveStepInProgressIndex}
                fetchedOnboardingImages={props.fetchedOnboardingImages}
            />
            <InitialSetupStep
                stepIndex={getStepIndex(BmweOnboardingSteps.STEP_INITIAL_SETUP)}
                menuExpanded={checkIfMenuExpanded(
                    props.lastCompletedStepIndex,
                    getStepIndex(BmweOnboardingSteps.STEP_INITIAL_SETUP),
                    props.hasIbr
                )}
                stepCompleted={isStepCompleted(
                    getStepIndex(BmweOnboardingSteps.STEP_INITIAL_SETUP),
                    props.lastCompletedStepIndex
                )}
                stepActive={false}
                stepClickable={checkIfStepIsClickable(
                    getStepIndex(BmweOnboardingSteps.STEP_INITIAL_SETUP),
                    props.lastCompletedStepIndex,
                    props.hasIbr
                )}
                saveOnboardingStep={props.saveOnboardingStep}
                stepInProgressIndex={props.stepInProgressIndex}
                saveStepInProgressIndex={props.saveStepInProgressIndex}
            />
            <RunErasure
                stepIndex={getStepIndex(BmweOnboardingSteps.STEP_RUNNING_ERASURE)}
                menuExpanded={checkIfMenuExpanded(
                    props.lastCompletedStepIndex,
                    getStepIndex(BmweOnboardingSteps.STEP_RUNNING_ERASURE),
                    props.hasIbr
                )}
                stepActive={false}
                product={OnboardingProductType.BMWE}
                stepClickable={checkIfStepIsClickable(
                    getStepIndex(BmweOnboardingSteps.STEP_RUNNING_ERASURE),
                    props.lastCompletedStepIndex,
                    props.hasIbr
                )}
                stepCompleted={isStepCompleted(
                    getStepIndex(BmweOnboardingSteps.STEP_RUNNING_ERASURE),
                    props.lastCompletedStepIndex
                )}
                saveOnboardingStep={props.saveOnboardingStep}
                hasIbr={props.hasIbr}
                stepInProgressIndex={props.stepInProgressIndex}
                saveStepInProgressIndex={props.saveStepInProgressIndex}
            />
            {props.canViewReport && (
                <ViewReport
                    stepIndex={getStepIndex(BmweOnboardingSteps.STEP_VIEWING_REPORT)}
                    menuExpanded={checkIfMenuExpanded(
                        props.lastCompletedStepIndex,
                        getStepIndex(BmweOnboardingSteps.STEP_VIEWING_REPORT),
                        props.hasIbr
                    )}
                    stepCompleted={isStepCompleted(
                        getStepIndex(BmweOnboardingSteps.STEP_VIEWING_REPORT),
                        props.lastCompletedStepIndex
                    )}
                    stepActive={false}
                    stepClickable={checkIfStepIsClickable(
                        getStepIndex(BmweOnboardingSteps.STEP_VIEWING_REPORT),
                        props.lastCompletedStepIndex,
                        props.hasIbr
                    )}
                    product={OnboardingProductType.BMWE}
                    hasIbr={props.hasIbr}
                    saveOnboardingStep={props.saveOnboardingStep}
                />
            )}
        </>
    );
};

export default connector(BmweSteps);
